import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';

import { ValidationErrors } from '@angular/forms';
import { NzPopoverDirective } from 'ng-zorro-antd/popover';
import { isNil, isObject } from 'lodash';

export type ErrorMessages = ReadonlyArray<{ error: string; errorMsg: string }>;

export type ErrorVerticalMessagePosition =
  | 'inside'
  | 'outside'
  | 'insideDate'
  | 'leftAlign'
  | 'suffixPrefix'
  | 'outsideRight'
  | 'outsideLeft'
  | 'forSelect';

@Component({
  selector: 'vpfa-field-validation-container',
  templateUrl: './field-validation-container.component.html',
  styleUrls: ['./field-validation-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldValidationContainerComponent implements OnDestroy {
  intervalId;

  @Input() set isInvalid(isInvalid: boolean) {
    this._isInvalid = isInvalid;
    this.handleIsInvalidState(isInvalid);
  }

  public get isInvalid(): boolean {
    return this._isInvalid;
  }

  @Input()
  set heightCompensationPx(heightCompensationPx: string | number) {
    if (!isNil(heightCompensationPx)) {
      this._heightCompensationPx = heightCompensationPx;
    }
  }

  @Input() isText: boolean;
  @Input() isNumber: boolean;
  @Input() isCurrency: boolean;

  private _heightCompensationPx: string | number = 0;
  private _isInvalid = false;

  get heightCompensationPx(): string | number {
    return this._heightCompensationPx;
  }

  hostBoundingRect: ClientRect | DOMRect;
  _errorMsgVertPosition: number;

  @Input() set errorMsgVertPosition(position: ErrorVerticalMessagePosition) {
    this.updateVerticalPosition(position);
  }

  @Input()
  set validationErrors(setValue: ValidationErrors) {
    this._validationErrors = setValue;
    this.popoverContent = this.getPopoverContent();
  }

  @Input() set errorMessages(messages: ErrorMessages) {
    this._errorMessages = messages;
    this.popoverContent = this.getPopoverContent();
  }

  @Input() displayErrors = true;
  @Input() errorPopoverPlacement: 'top' | 'topRight' = 'top';

  @ViewChild(NzPopoverDirective, { read: NzPopoverDirective }) tooltipCtrlRef: NzPopoverDirective;
  popoverContent: string = null;
  errorType: string = '';
  errorSignTopPos = 3;
  private _errorMessages: ErrorMessages;
  private _validationErrors: ValidationErrors;

  errorData: object | null = null;

  constructor(private changeDetectorRef: ChangeDetectorRef) {}

  getPopoverContent() {
    if (this._validationErrors && this._errorMessages) {
      for (const entry of this._errorMessages) {
        if (this._validationErrors[entry.error]) {
          this.errorType = entry.error + 'ErrorType';

          if (isObject(this._validationErrors[entry.error])) {
            this.errorData = this._validationErrors[entry.error];
          } else {
            this.errorData = null;
          }

          return entry.errorMsg;
        }
      }
    }

    this.errorType = '';
  }

  updateErrorSignPosition(rect: ClientRect | DOMRect) {
    if (rect && rect.height !== 0) {
      this.hostBoundingRect = rect;
      this.errorSignTopPos =
        Math.floor(this.hostBoundingRect.height / 2) + parseInt(String(this.heightCompensationPx), 10);
    }
  }

  ngOnDestroy(): void {
    this.stopPopoverPositionUpdate();
  }

  /**
   * Updates popover position in intervals
   * required when validation is done in modal
   */
  private startPopoverPositionUpdate() {
    if (isNil(this.intervalId)) {
      this.intervalId = setInterval(() => {
        this.tooltipCtrlRef.updatePosition();
      }, 100);
    }
  }

  private stopPopoverPositionUpdate() {
    if (!isNil(this.intervalId)) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private updateVerticalPosition(position: ErrorVerticalMessagePosition) {
    if (!position) {
      this._errorMsgVertPosition = -25;
      this.changeDetectorRef.markForCheck();
      return;
    }
    switch (position) {
      case 'inside':
        this._errorMsgVertPosition = 5;
        break;
      case 'forSelect':
        this._errorMsgVertPosition = 30;
        break;
      case 'insideDate':
        this._errorMsgVertPosition = 25;
        break;
      case 'outside':
        this._errorMsgVertPosition = 7;
        break;
      case 'leftAlign':
        this._errorMsgVertPosition = 147;
        break;
      case 'outsideLeft':
        this._errorMsgVertPosition = 175;
        break;
      case 'suffixPrefix':
        this._errorMsgVertPosition = 215;
        break;
      case 'outsideRight':
        this._errorMsgVertPosition = -25;
        break;
    }
    this.changeDetectorRef.markForCheck();
  }

  private handleIsInvalidState(isInvalid: boolean) {
    if (isInvalid) {
      this.startPopoverPositionUpdate();
    } else {
      this.stopPopoverPositionUpdate();
    }
  }
}
